/* The console's map: real Google Maps (the same loader, key and libraries as
   the classic live map, so no new API and no new cost line), painted in the
   design's map colours for day and night, with the design's own markers —
   the moving bus with its teal pulse, the ringed late / idle bus, the school.
   Map camera moves take 600 ms, the only long move in the console. */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_VERSION } from '../utils/googleMapsLoader';
import type { Bus } from './data/model';
import { busWithPlate } from './kit';

const NAIROBI = { lat: -1.286, lng: 36.817 };

/* The canvas map tokens (--m-*) as Google map styles. */
export function mapStyles(dark: boolean): google.maps.MapTypeStyle[] {
  const c = dark
    ? { land: '#141A1F', bld: '#1B232A', park: '#17231E', water: '#1B2A31', minor: '#222B33', road: '#2E3943', art: '#3A4651', artC: '#0E1317', label: '#6F7C86', hood: '#55616B' }
    : { land: '#F3F1EC', bld: '#E5E2DA', park: '#D4E6C9', water: '#BFDCE6', minor: '#FFFFFF', road: '#FFFFFF', art: '#FCE3A0', artC: '#E9C46A', label: '#5E6166', hood: '#9A968C' };
  return [
    { elementType: 'geometry', stylers: [{ color: c.land }] },
    { elementType: 'labels.text.fill', stylers: [{ color: c.label }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: c.land }, { weight: 3 }] },
    { featureType: 'administrative.neighborhood', elementType: 'labels.text.fill', stylers: [{ color: c.hood }] },
    { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: c.bld }] },
    { featureType: 'poi', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: c.park }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: c.water }] },
    { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: c.minor }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: dark ? '#11171C' : '#E2DED5' }] },
    { featureType: 'road.arterial', elementType: 'geometry.fill', stylers: [{ color: c.road }] },
    { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: c.art }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: c.artC }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ];
}

/* ── markers (drawn as in the design) ── */
const MovingBus: React.FC<{ selected: boolean; heading: number | null }> = ({ selected, heading }) => (
  <svg width="72" height="72" viewBox="-36 -36 72 72" style={{ overflow: 'visible', display: 'block' }} aria-hidden="true">
    <circle r="26" fill="#0FB8CE" className="pulse" />
    {selected && <circle r="22" fill="none" stroke="#0FB8CE" strokeWidth="3" />}
    <g transform={`rotate(${heading != null ? heading - 90 : 0}) scale(1.8) translate(-17 -8)`}>
      <rect x="0" y="0" width="34" height="16" rx="4" fill="#0F1419" />
      <rect x="27" y="2" width="5" height="12" rx="1.5" fill="#9FDCE5" />
      <rect x="5" y="3" width="19" height="10" rx="1.5" fill="#FFC83D" />
      <path d="M8 3v10M12 3v10M16 3v10M20 3v10" stroke="#E0A800" strokeWidth="1" />
      <rect x="1" y="5" width="2" height="6" rx="1" fill="#F04B3A" />
    </g>
  </svg>
);
const RingBus: React.FC<{ color: string; selected: boolean }> = ({ color, selected }) => (
  <svg width="40" height="40" viewBox="-20 -20 40 40" style={{ overflow: 'visible', display: 'block' }} aria-hidden="true">
    {selected && <circle r="20" fill="none" stroke={color} strokeWidth="2" opacity=".5" />}
    <circle r="16" fill="#fff" stroke={color} strokeWidth="3" />
    <path d="M-6 -4h12v8h-12z" fill={color} />
  </svg>
);
const School: React.FC = () => (
  <svg width="26" height="26" viewBox="-13 -13 26 26" style={{ overflow: 'visible', display: 'block' }} aria-hidden="true">
    <circle r="11" fill="#111520" stroke="#fff" strokeWidth="3" />
    <path d="M-4 3V-2l4-2.5 4 2.5v5" fill="none" stroke="#fff" strokeWidth="1.4" />
  </svg>
);
const centre = (w: number, h: number) => ({ x: -w / 2, y: -h / 2 });

export interface MapViewProps {
  buses: Bus[];
  gate?: { lat: number; lng: number } | null;
  dark: boolean;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** false: a still preview (no gestures, no controls), as on Overview. */
  interactive?: boolean;
  /** Pan to the selected bus when it changes. */
  follow?: boolean;
  style?: React.CSSProperties;
  label?: string;
}

const MapView: React.FC<MapViewProps> = ({ buses, gate, dark, selectedId, onSelect, interactive = true, follow = true, style, label = 'Map of your buses' }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded, loadError } = useJsApiLoader({ id: GOOGLE_MAPS_SCRIPT_ID, googleMapsApiKey: apiKey, libraries: GOOGLE_MAPS_LIBRARIES, version: GOOGLE_MAPS_VERSION });
  const mapRef = useRef<google.maps.Map | null>(null);
  const placed = useMemo(() => buses.filter((b) => b.lat != null && b.lng != null), [buses]);
  const fitted = useRef(false);

  const fit = useCallback(() => {
    const m = mapRef.current;
    if (!m || !window.google) return;
    const pts = placed.map((b) => ({ lat: b.lat as number, lng: b.lng as number }));
    if (gate) pts.push(gate);
    if (!pts.length) { m.setCenter(NAIROBI); m.setZoom(12); return; }
    if (pts.length === 1) { m.setCenter(pts[0]); m.setZoom(14); return; }
    const bounds = new google.maps.LatLngBounds();
    pts.forEach((p) => bounds.extend(p));
    m.fitBounds(bounds, 48);
  }, [placed, gate]);

  useEffect(() => { if (isLoaded && !fitted.current && (placed.length || gate)) { fit(); fitted.current = true; } }, [isLoaded, placed.length, gate, fit]);
  useEffect(() => {
    if (!follow || !selectedId || !mapRef.current) return;
    const b = placed.find((x) => x.id === selectedId);
    if (b) mapRef.current.panTo({ lat: b.lat as number, lng: b.lng as number });
  }, [selectedId, follow, placed]);
  useEffect(() => { mapRef.current?.setOptions({ styles: mapStyles(dark) }); }, [dark]);

  const shell: React.CSSProperties = { position: 'relative', width: '100%', height: '100%', background: dark ? '#141A1F' : '#F3F1EC', ...style };
  if (!apiKey || loadError) {
    return (
      <div style={{ ...shell, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 14, textAlign: 'center', padding: 24 }} role="img" aria-label={label}>
        {!apiKey ? 'The map needs its Google Maps key on this site.' : 'The map could not load. Bus positions are still being recorded.'}
      </div>
    );
  }
  if (!isLoaded) return <div className="shim" style={{ ...shell, borderRadius: 0 }} aria-label="Loading map" />;

  return (
    <div style={shell} role="region" aria-label={label}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={NAIROBI}
        zoom={12}
        onLoad={(m) => { mapRef.current = m; fitted.current = false; m.setOptions({ styles: mapStyles(dark) }); if (placed.length || gate) { fit(); fitted.current = true; } }}
        onUnmount={() => { mapRef.current = null; }}
        options={{
          disableDefaultUI: true, zoomControl: interactive, gestureHandling: interactive ? 'greedy' : 'none',
          keyboardShortcuts: interactive, clickableIcons: false, styles: mapStyles(dark),
        }}
      >
        {gate && (
          <OverlayView position={gate} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={centre}>
            <div title="School"><School /></div>
          </OverlayView>
        )}
        {placed.map((b) => {
          const sel = b.id === selectedId;
          const moving = b.status === 'on_route';
          const color = b.status === 'late' ? '#985A00' : b.status === 'off_road' ? '#C23B3B' : '#56606B';
          return (
            <OverlayView key={b.id} position={{ lat: b.lat as number, lng: b.lng as number }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={centre}>
              <button type="button" onClick={() => onSelect?.(b.id)} aria-label={`${b.name}, ${b.plate}, ${b.statusLabel}`} title={busWithPlate(b)}
                tabIndex={interactive ? 0 : -1}
                style={{ all: 'unset', cursor: onSelect ? 'pointer' : 'default', display: 'block', transition: 'transform 600ms cubic-bezier(.2,0,0,1)' }}>
                {moving ? <MovingBus selected={sel} heading={b.heading} /> : <RingBus color={color} selected={sel} />}
              </button>
            </OverlayView>
          );
        })}
      </GoogleMap>
      {interactive && (
        <span style={{ position: 'absolute', right: 8, bottom: 6, fontSize: 11, color: 'var(--faint)', pointerEvents: 'none' }}>{placed.length} of {buses.length} buses on the map</span>
      )}
    </div>
  );
};

export default MapView;
